import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  X,
  Mail,
  FileText,
  Receipt,
  Send,
  Plus,
  Trash2,
  Loader2,
  CheckCircle2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const STATUS_FLOW = [
  { key: "new", label: "New", color: "#E65C00" },
  { key: "quoted", label: "Quoted", color: "#8B5A2B" },
  { key: "scheduled", label: "Scheduled", color: "#1B5E20" },
  { key: "in_progress", label: "In Progress", color: "#2196F3" },
  { key: "completed", label: "Completed", color: "#1B2A22" },
  { key: "paid", label: "Paid", color: "#2E7D32" },
  { key: "lost", label: "Lost", color: "#9E9E9E" },
];

export function StatusBadge({ status }) {
  const s = STATUS_FLOW.find((x) => x.key === (status || "new")) || STATUS_FLOW[0];
  return (
    <span
      className="px-2 py-1 border-2 border-forest text-[10px] uppercase tracking-[0.2em] font-bold text-white"
      style={{ backgroundColor: s.color }}
    >
      {s.label}
    </span>
  );
}

const EMAIL_TEMPLATES = {
  followup: {
    label: "Friendly Follow-Up",
    subject: "Following up on your tree quote",
    body:
      "Hi {{name}},\n\nJust checking in on the quote we discussed for your property at {{address}}. " +
      "Let me know if you have any questions or if you'd like to set a date for the work.\n\n" +
      "Thanks for considering Acorn Tree Service²!",
  },
  schedule: {
    label: "Schedule Confirmation",
    subject: "Job scheduled — see you soon!",
    body:
      "Hi {{name}},\n\nConfirming we have your tree work scheduled. We'll be at {{address}} as planned. " +
      "Please make sure the area is accessible for our crew & equipment.\n\n" +
      "Call/text me at (865) 621-0995 if anything changes.\n\nThanks!",
  },
  complete: {
    label: "Job Complete — Thank You",
    subject: "Thanks for choosing Acorn Tree Service²",
    body:
      "Hi {{name}},\n\nJust wanted to say thank you for trusting us with the work at {{address}}. " +
      "If you're happy with how it turned out, a quick Google review would mean the world to us!\n\n" +
      "We'd love to help with any future tree work — feel free to share our number with neighbors.\n\n" +
      "— Acorn Tree Service²",
  },
  estimate: {
    label: "Estimate Coming Soon",
    subject: "Your estimate is on the way",
    body:
      "Hi {{name}},\n\nThanks for reaching out about the tree work at {{address}}. " +
      "I'll have your detailed estimate over to you shortly. " +
      "If anything changes or you have questions, just call/text me at (865) 621-0995.\n\n" +
      "— Acorn Tree Service²",
  },
};

function fill(template, lead) {
  return template
    .replace(/\{\{name\}\}/g, lead.name || "there")
    .replace(/\{\{address\}\}/g, lead.address || "your property")
    .replace(/\{\{phone\}\}/g, lead.phone || "");
}

export function EmailModal({ lead, token, onClose, onSent }) {
  const [tplKey, setTplKey] = useState("followup");
  const [subject, setSubject] = useState(
    fill(EMAIL_TEMPLATES.followup.subject, lead),
  );
  const [body, setBody] = useState(fill(EMAIL_TEMPLATES.followup.body, lead));
  const [sending, setSending] = useState(false);

  const pickTemplate = (key) => {
    setTplKey(key);
    const t = EMAIL_TEMPLATES[key];
    setSubject(fill(t.subject, lead));
    setBody(fill(t.body, lead));
  };

  const send = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error("Subject and message required");
      return;
    }
    setSending(true);
    try {
      await axios.post(
        `${API}/quotes/${lead.id}/email`,
        { subject, body },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Sent to ${lead.email}`);
      onSent?.();
      onClose();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not send. Make sure the lead has an email.",
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      role="dialog"
      data-testid="email-modal"
      className="fixed inset-0 z-[80] bg-black/80 flex items-end sm:items-center justify-center p-0 sm:p-6"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-2xl bg-bone border-2 border-forest hard-shadow-lg max-h-[95vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-forest text-bone p-4 flex items-center justify-between border-b-2 border-forest">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-safety font-bold">
              Email Customer
            </div>
            <div className="font-display text-2xl uppercase tracking-tight">
              {lead.name}
            </div>
            <div className="text-xs text-bone/70 mt-1">{lead.email || "no email on file"}</div>
          </div>
          <button onClick={onClose} className="text-bone hover:text-safety" aria-label="Close" type="button">
            <X className="w-6 h-6" strokeWidth={3} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="label-brutal">Quick Template</label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(EMAIL_TEMPLATES).map(([k, t]) => (
                <button
                  key={k}
                  type="button"
                  data-testid={`email-template-${k}`}
                  onClick={() => pickTemplate(k)}
                  className={`px-3 py-2 border-2 border-forest text-xs uppercase tracking-widest font-bold ${
                    tplKey === k ? "bg-safety text-white" : "bg-white text-forest hover:bg-muted2"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="label-brutal" htmlFor="email-subject">Subject</label>
            <input
              id="email-subject"
              data-testid="email-subject-input"
              className="input-brutal"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>

          <div>
            <label className="label-brutal" htmlFor="email-body">Message</label>
            <textarea
              id="email-body"
              data-testid="email-body-input"
              className="input-brutal min-h-[200px] resize-y"
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
            <p className="text-[11px] text-forest/60 mt-2 uppercase tracking-widest">
              Sent from acorntreeserv2@gmail.com via Resend · Reply-to: customer email
            </p>
          </div>

          <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end pt-2">
            <button onClick={onClose} type="button" className="btn-dark px-5 py-3" style={{ background: "#1B2A22", color: "#F4F1ED" }}>
              CANCEL
            </button>
            <button
              type="button"
              data-testid="email-send-button"
              onClick={send}
              disabled={sending || !lead.email}
              className="btn-primary px-6 py-3 flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" strokeWidth={3} />}
              {sending ? "SENDING..." : "SEND EMAIL"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const CATEGORIES = {
  tree: {
    label: "Tree Service",
    icon: "🌳",
    lines: [
      { description: "Trim small tree", unit_price: 175 },
      { description: "Trim medium tree", unit_price: 300 },
      { description: "Trim large tree (powerline area)", unit_price: 550 },
      { description: "Tree Removal", unit_price: 750 },
      { description: "Dangerous Tree Removal", unit_price: 1200 },
      { description: "Stump Grinding", unit_price: 150 },
      { description: "Underbrush Cleanup (per area)", unit_price: 225 },
      { description: "Lot Clearing (per hour)", unit_price: 250 },
      { description: "Haul to Dump", unit_price: 375 },
      { description: "Debris Haul-Off (truckload)", unit_price: 200 },
      { description: "Storm Cleanup (per hour)", unit_price: 200 },
      { description: "Bucket Truck (per hour)", unit_price: 175 },
      { description: "Climbing Labor (per hour)", unit_price: 95 },
      { description: "Ground Crew (per man-hour)", unit_price: 55 },
    ],
  },
  construction: {
    label: "Construction",
    icon: "🏗️",
    lines: [
      { description: "Demo Work (per hour)", unit_price: 85 },
      { description: "Excavation / Grading (per hour)", unit_price: 150 },
      { description: "Concrete Work (per sq ft)", unit_price: 8 },
      { description: "Framing Labor (per hour)", unit_price: 65 },
      { description: "Roofing (per square)", unit_price: 400 },
      { description: "Fencing (per linear ft)", unit_price: 30 },
      { description: "Driveway Repair", unit_price: 750 },
      { description: "Foundation Work (per hour)", unit_price: 95 },
      { description: "Skid Steer Work (per hour)", unit_price: 150 },
      { description: "Site Prep", unit_price: 500 },
    ],
  },
  repair: {
    label: "Home Repair",
    icon: "🔨",
    lines: [
      { description: "General Labor (per hour)", unit_price: 55 },
      { description: "Drywall Repair", unit_price: 250 },
      { description: "Painting (per room)", unit_price: 350 },
      { description: "Trim / Molding", unit_price: 200 },
      { description: "Plumbing Repair", unit_price: 150 },
      { description: "Electrical Repair", unit_price: 175 },
      { description: "Door Install", unit_price: 200 },
      { description: "Window Install", unit_price: 250 },
      { description: "Deck Repair", unit_price: 500 },
      { description: "Pressure Washing", unit_price: 200 },
      { description: "Gutter Cleaning", unit_price: 175 },
    ],
  },
  hauling: {
    label: "Hauling / Materials",
    icon: "🚚",
    lines: [
      { description: "Hauling (per truckload)", unit_price: 250 },
      { description: "Dump Fees", unit_price: 75 },
      { description: "Equipment Rental (per day)", unit_price: 300 },
      { description: "Material Delivery", unit_price: 150 },
      { description: "Yard / Junk Cleanup", unit_price: 200 },
      { description: "Mulch (per yard)", unit_price: 65 },
      { description: "Gravel (per yard)", unit_price: 55 },
      { description: "Topsoil (per yard)", unit_price: 45 },
    ],
  },
};

function defaultCategoryForLead(lead) {
  const s = (lead.service_type || "").toLowerCase();
  if (s.includes("tree") || s.includes("storm") || s.includes("lot") || s.includes("debris")) return "tree";
  if (s.includes("demo") || s.includes("concrete") || s.includes("excav") || s.includes("framing") || s.includes("roof") || s.includes("fenc")) return "construction";
  if (s.includes("repair") || s.includes("paint") || s.includes("drywall") || s.includes("plumb") || s.includes("electric")) return "repair";
  if (s.includes("haul") || s.includes("deliver") || s.includes("dump")) return "hauling";
  return "tree";
}

export function InvoiceModal({ lead, token, kind, onClose, onSent }) {
  const initial = lead.service_type
    ? [{ description: lead.service_type, quantity: 1, unit_price: 0 }]
    : [{ description: "", quantity: 1, unit_price: 0 }];
  const [items, setItems] = useState(initial);
  const [category, setCategory] = useState(defaultCategoryForLead(lead));
  const [taxPct, setTaxPct] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [discountLabel, setDiscountLabel] = useState("Cash Discount");
  const [note, setNote] = useState(
    kind === "quote"
      ? "Free on-site estimate. Price valid 30 days. Includes cleanup."
      : "Thanks for your business! Payable by cash, check, or card.",
  );
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);

  const updateItem = (idx, field, value) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));

  const addItem = () =>
    setItems((prev) => [...prev, { description: "", quantity: 1, unit_price: 0 }]);
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));
  const pickCommon = (idx, line) =>
    setItems((prev) =>
      prev.map((it, i) => (i === idx ? { ...it, description: line.description, unit_price: line.unit_price } : it)),
    );

  const subtotal = items.reduce(
    (s, it) => s + Number(it.quantity || 0) * Number(it.unit_price || 0),
    0,
  );
  const safeDiscount = Math.min(Math.max(0, Number(discount) || 0), subtotal);
  const afterDiscount = subtotal - safeDiscount;
  const tax = afterDiscount * (Number(taxPct || 0) / 100);
  const total = afterDiscount + tax;

  const send = async () => {
    const validItems = items
      .filter((it) => it.description.trim() && Number(it.unit_price) > 0)
      .map((it) => ({
        description: it.description.trim(),
        quantity: Number(it.quantity) || 1,
        unit_price: Number(it.unit_price),
      }));
    if (validItems.length === 0) {
      toast.error("Add at least one line item with a price");
      return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/quotes/${lead.id}/invoice`,
        {
          kind,
          items: validItems,
          tax_pct: Number(taxPct) || 0,
          discount: Number(discount) || 0,
          discount_label: discountLabel || "Discount",
          note: note.trim() || null,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`${kind === "quote" ? "Quote" : "Invoice"} ${data.doc_number} sent — $${data.total.toFixed(2)}`);
      setSent({
        docNumber: data.doc_number,
        total: data.total,
        url: `${window.location.origin}/invoice/${lead.id}/${data.id}`,
      });
      onSent?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send");
    } finally {
      setSending(false);
    }
  };

  const isQuote = kind === "quote";
  return (
    <div
      role="dialog"
      data-testid={`${kind}-modal`}
      className="fixed inset-0 z-[80] bg-black/80 flex items-end sm:items-center justify-center p-0 sm:p-6"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-3xl bg-bone border-2 border-forest hard-shadow-lg max-h-[95vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-forest text-bone p-4 flex items-center justify-between border-b-2 border-forest">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-safety font-bold">
              {isQuote ? "Send Quote" : "Send Invoice"}
            </div>
            <div className="font-display text-2xl uppercase tracking-tight">{lead.name}</div>
            <div className="text-xs text-bone/70 mt-1">
              {lead.email || "no email on file"} · {lead.address}
            </div>
          </div>
          <button onClick={onClose} className="text-bone hover:text-safety" aria-label="Close" type="button">
            <X className="w-6 h-6" strokeWidth={3} />
          </button>
        </div>

        <div className="p-4 sm:p-5 space-y-4">
          {/* Category Tabs */}
          <div>
            <div className="text-xs uppercase tracking-[0.25em] font-bold text-forest/70 mb-2">
              Job Category — pick your price list
            </div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(CATEGORIES).map(([key, cat]) => (
                <button
                  key={key}
                  type="button"
                  data-testid={`category-${key}`}
                  onClick={() => setCategory(key)}
                  className={`px-3 py-2 border-2 border-forest text-xs uppercase tracking-widest font-bold flex items-center gap-1.5 ${
                    category === key ? "bg-safety text-white" : "bg-bone text-forest hover:bg-muted2"
                  }`}
                >
                  <span className="text-base leading-none">{cat.icon}</span> {cat.label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            {items.map((it, idx) => (
              <div key={idx} className="border-2 border-forest bg-white p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs uppercase tracking-widest font-bold text-forest/70">
                    Line {idx + 1}
                  </span>
                  {items.length > 1 && (
                    <button
                      type="button"
                      data-testid={`remove-item-${idx}`}
                      onClick={() => removeItem(idx)}
                      className="text-safety hover:text-red-600"
                      aria-label="Remove line"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <input
                  className="input-brutal mb-2"
                  data-testid={`item-description-${idx}`}
                  placeholder="Description (e.g. Oak removal, 60ft)"
                  value={it.description}
                  onChange={(e) => updateItem(idx, "description", e.target.value)}
                />
                <div className="grid grid-cols-2 gap-2">
                  <input
                    className="input-brutal"
                    data-testid={`item-qty-${idx}`}
                    type="number"
                    step="0.5"
                    min="0"
                    placeholder="Qty"
                    value={it.quantity}
                    onChange={(e) => updateItem(idx, "quantity", e.target.value)}
                  />
                  <input
                    className="input-brutal"
                    data-testid={`item-price-${idx}`}
                    type="number"
                    step="1"
                    min="0"
                    placeholder="Unit $"
                    value={it.unit_price}
                    onChange={(e) => updateItem(idx, "unit_price", e.target.value)}
                  />
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {(CATEGORIES[category]?.lines || []).map((c) => (
                    <button
                      type="button"
                      key={c.description}
                      onClick={() => pickCommon(idx, c)}
                      className="text-[10px] uppercase tracking-widest font-bold border-2 border-forest/30 bg-bone hover:bg-muted2 px-2 py-1"
                    >
                      + {c.description} (${c.unit_price})
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <button
              type="button"
              data-testid="add-line-item"
              onClick={addItem}
              className="btn-dark w-full py-3 flex items-center justify-center gap-2"
              style={{ background: "#1B2A22", color: "#F4F1ED" }}
            >
              <Plus className="w-4 h-4" strokeWidth={3} /> ADD LINE
            </button>
          </div>

          <div className="border-2 border-forest bg-bone p-3 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="font-bold uppercase tracking-widest text-forest/70 text-xs">Subtotal</span>
              <span className="font-display text-xl tracking-tight">${subtotal.toFixed(2)}</span>
            </div>

            <div className="grid grid-cols-[1fr_auto] gap-2 items-center">
              <div>
                <label className="label-brutal mb-1" htmlFor="discount-label">Discount label</label>
                <input
                  id="discount-label"
                  data-testid="discount-label-input"
                  className="input-brutal"
                  type="text"
                  placeholder="e.g. Cash Discount, Pay-Today Deal"
                  value={discountLabel}
                  onChange={(e) => setDiscountLabel(e.target.value)}
                />
              </div>
              <div className="w-28">
                <label className="label-brutal mb-1" htmlFor="discount-amt">Amount $</label>
                <input
                  id="discount-amt"
                  data-testid="discount-amount-input"
                  className="input-brutal"
                  type="number"
                  step="1"
                  min="0"
                  placeholder="0"
                  value={discount}
                  onChange={(e) => setDiscount(e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-brutal mb-1" htmlFor="tax-pct">Tax %</label>
                <input
                  id="tax-pct"
                  data-testid="tax-pct-input"
                  className="input-brutal"
                  type="number"
                  step="0.25"
                  min="0"
                  max="100"
                  value={taxPct}
                  onChange={(e) => setTaxPct(e.target.value)}
                />
              </div>
              <div>
                <label className="label-brutal mb-1">Customer Pays</label>
                <div className="bg-safety text-white font-display text-3xl tracking-tight px-4 py-2 border-2 border-forest">
                  ${total.toFixed(2)}
                </div>
              </div>
            </div>

            {safeDiscount > 0 && (
              <div className="text-xs text-forest/70 text-right">
                Subtotal ${subtotal.toFixed(2)} · {discountLabel || "Discount"} −${safeDiscount.toFixed(2)}
                {Number(taxPct) > 0 && ` · Tax $${tax.toFixed(2)}`}
              </div>
            )}
          </div>

          <div>
            <label className="label-brutal" htmlFor="invoice-note">Note (optional)</label>
            <textarea
              id="invoice-note"
              data-testid="invoice-note-input"
              className="input-brutal min-h-[80px] resize-y"
              maxLength={1000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>

          {sent ? (
            <div className="border-2 border-forest bg-bone p-5">
              <div className="text-xs uppercase tracking-[0.3em] font-bold text-safety mb-2">
                ✓ Sent to email & saved
              </div>
              <div className="font-display text-2xl tracking-tight mb-3">
                {(kind === "quote" ? "Quote" : "Invoice")} {sent.docNumber} · ${sent.total.toFixed(2)}
              </div>
              <p className="text-sm text-forest/80 mb-4">
                Now send this {kind} to {lead.name} as a text message too — they tap the link, see the full {kind} on their phone.
              </p>
              <a
                href={`sms:${(lead.phone || "").replace(/[^\d+]/g, "")}?&body=${encodeURIComponent(
                  `Hi ${lead.name}, here's your ${kind} from Acorn Tree Service²: ${sent.url}`,
                )}`}
                data-testid={`${kind}-text-link`}
                className="btn-primary w-full py-4 text-lg flex items-center justify-center gap-2"
              >
                📱 TEXT THIS {kind === "quote" ? "QUOTE" : "INVOICE"} TO {lead.name.split(" ")[0].toUpperCase()}
              </a>
              <div className="grid grid-cols-2 gap-2 mt-3">
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(sent.url);
                    toast.success("Link copied to clipboard");
                  }}
                  className="btn-dark py-3 text-sm flex items-center justify-center gap-2"
                  style={{ background: "#1B2A22", color: "#F4F1ED" }}
                  data-testid={`${kind}-copy-link`}
                >
                  COPY LINK
                </button>
                <a
                  href={sent.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-dark py-3 text-sm flex items-center justify-center gap-2"
                  style={{ background: "#8B5A2B", color: "#F4F1ED" }}
                  data-testid={`${kind}-preview-link`}
                >
                  PREVIEW
                </a>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="w-full mt-3 text-xs uppercase tracking-widest text-forest/60 hover:text-forest font-bold py-2"
              >
                Close
              </button>
            </div>
          ) : (
          <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end pt-2">
            <button onClick={onClose} type="button" className="btn-dark px-5 py-3" style={{ background: "#1B2A22", color: "#F4F1ED" }}>
              CANCEL
            </button>
            <button
              type="button"
              data-testid={`${kind}-send-button`}
              onClick={send}
              disabled={sending || !lead.email}
              className="btn-primary px-6 py-3 flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" strokeWidth={3} />}
              {sending ? "SENDING..." : isQuote ? "SEND QUOTE" : "SEND INVOICE"}
            </button>
          </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function StatusPicker({ lead, token, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const current = lead.status || "new";

  const setStatus = async (newStatus) => {
    if (newStatus === current) {
      setOpen(false);
      return;
    }
    setBusy(true);
    try {
      await axios.patch(
        `${API}/quotes/${lead.id}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Marked ${STATUS_FLOW.find((s) => s.key === newStatus)?.label}`);
      onChanged?.(newStatus);
    } catch {
      toast.error("Could not update status");
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  return (
    <div className="relative inline-block">
      <button
        type="button"
        data-testid={`status-btn-${lead.id}`}
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
        className="flex items-center gap-1"
        aria-label="Change status"
      >
        <StatusBadge status={current} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} />
          <div className="absolute z-[70] mt-1 left-0 bg-white border-2 border-forest hard-shadow min-w-[180px]">
            {STATUS_FLOW.map((s) => (
              <button
                key={s.key}
                type="button"
                data-testid={`status-option-${s.key}`}
                onClick={() => setStatus(s.key)}
                className="w-full text-left px-3 py-2 hover:bg-muted2 flex items-center gap-2 text-sm uppercase tracking-widest font-bold"
              >
                <span
                  className="w-3 h-3 border-2 border-forest"
                  style={{ backgroundColor: s.color }}
                ></span>
                {s.label}
                {s.key === current && <CheckCircle2 className="w-3 h-3 ml-auto text-safety" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function LeadActions({ lead, token, onChanged }) {
  const [emailOpen, setEmailOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const noEmail = !lead.email;

  return (
    <>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          data-testid={`action-email-${lead.id}`}
          onClick={() => setEmailOpen(true)}
          disabled={noEmail}
          title={noEmail ? "No email on file" : "Email customer"}
          className="btn-dark px-3 py-2 text-xs flex items-center gap-1 disabled:opacity-50"
          style={{ background: "#1B2A22", color: "#F4F1ED" }}
        >
          <Mail className="w-3.5 h-3.5" strokeWidth={3} /> EMAIL
        </button>
        <button
          type="button"
          data-testid={`action-quote-${lead.id}`}
          onClick={() => setQuoteOpen(true)}
          disabled={noEmail}
          className="btn-dark px-3 py-2 text-xs flex items-center gap-1 disabled:opacity-50"
          style={{ background: "#8B5A2B", color: "#F4F1ED" }}
        >
          <FileText className="w-3.5 h-3.5" strokeWidth={3} /> QUOTE
        </button>
        <button
          type="button"
          data-testid={`action-invoice-${lead.id}`}
          onClick={() => setInvoiceOpen(true)}
          disabled={noEmail}
          className="btn-primary px-3 py-2 text-xs flex items-center gap-1 disabled:opacity-50"
        >
          <Receipt className="w-3.5 h-3.5" strokeWidth={3} /> INVOICE
        </button>
      </div>
      {emailOpen && (
        <EmailModal lead={lead} token={token} onClose={() => setEmailOpen(false)} onSent={onChanged} />
      )}
      {quoteOpen && (
        <InvoiceModal lead={lead} token={token} kind="quote" onClose={() => setQuoteOpen(false)} onSent={onChanged} />
      )}
      {invoiceOpen && (
        <InvoiceModal lead={lead} token={token} kind="invoice" onClose={() => setInvoiceOpen(false)} onSent={onChanged} />
      )}
    </>
  );
}
